import { Boxes, Clock, Layers, Sprout } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  NEUTRAL_CARD_STYLE,
  ACCENT_CHIP_STYLE,
  accentVars,
  type AccentName,
} from "@/lib/home-accents";

interface Benefit {
  icon: LucideIcon;
  accent: AccentName;
  title: string;
  body: string;
}

const BENEFITS: Benefit[] = [
  {
    icon: Boxes,
    accent: "gold",
    title: "Everything Together",
    body: "Education, exercises, journals and practical recovery tools in one place.",
  },
  {
    icon: Clock,
    accent: "sky",
    title: "Your Own Pace",
    body: "There is no programme you have to complete and no pressure to recover on a particular timeline.",
  },
  {
    icon: Layers,
    accent: "plum",
    title: "Use What You Need",
    body: "Come back to different resources depending on what you are experiencing that day.",
  },
  {
    icon: Sprout,
    accent: "sage",
    title: "Always Growing",
    body: "New resources, exercises, journals and workshops continue to be added.",
  },
];

/**
 * "Why Recovery Trauma™?" — consolidated into the single argument the rest of
 * the homepage doesn't already make: everything in one structured place.
 * Survivor-led / trauma-informed messaging lives in Built With Care instead.
 */
export function OnePlaceSection() {
  return (
    <section aria-labelledby="one-place-heading" className="space-y-4">
      <header className="mx-auto max-w-2xl text-center">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--gold-deep)]">
          Why Recovery Trauma™?
        </p>
        <h2
          id="one-place-heading"
          className="mt-2 font-display text-lg leading-tight tracking-tight text-foreground sm:text-2xl"
        >
          Your Recovery Tools, Together in One Place
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
          Recovery can mean searching through books, websites, videos,
          worksheets, apps and different therapeutic skills trying to find
          something that helps.
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground sm:text-base">
          Recovery Trauma™ brings trauma education, guided exercises, journals,
          nervous-system practices and supportive resources together in one
          structured space.
        </p>
      </header>

      <div className="relative px-0 sm:px-8">
        <Carousel
          opts={{ align: "start", loop: true }}
          className="w-full"
          aria-label="Why Recovery Trauma™"
        >
          <CarouselContent className="-ms-3 sm:-ms-4">
            {BENEFITS.map(({ icon: Icon, accent, title, body }) => (
              <CarouselItem
                key={title}
                className="ps-3 basis-[85%] sm:basis-1/2 sm:ps-4 lg:basis-1/4"
              >
                <article
                  style={{ ...accentVars(accent), ...NEUTRAL_CARD_STYLE }}
                  className="flex h-full flex-col rounded-xl border p-3 shadow-[var(--shadow-soft)]"
                >
                  <span
                    style={ACCENT_CHIP_STYLE}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full"
                  >
                    <Icon className="h-4.5 w-4.5" strokeWidth={1.9} />
                  </span>
                  <h3 className="mt-2.5 font-display text-[13.5px] text-foreground">{title}</h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{body}</p>
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden sm:flex -start-2" />
          <CarouselNext className="hidden sm:flex -end-2" />
        </Carousel>
      </div>
    </section>
  );
}
